<template>
    <div v-if="code && user" class="codeDetailsPage">
        <section class="codeHero">
            <div class="heroCopy">
                <div class="eyebrow">{{ description }}</div>
                <h1>{{ code.BusName }}</h1>
                <p v-if="code.description" class="heroDescription">{{ code.description }}</p>
                <div class="heroMeta">
                    <span class="metaChip">{{ codeStyle }}</span>
                    <span v-if="shortcodeValue" class="metaChip">Shortcode: {{ shortcodeValue }}</span>
                    <span class="metaChip">Updated {{ formattedLastModified }}</span>
                </div>
            </div>
            <div class="heroActions">
                <a v-if="codeadurl" :href="codeadurl" target="_blank" rel="noopener noreferrer" class="btn btn-outline-primary">
                    Open Redirect
                </a>
                <router-link :to="'/editcode/' + this.codeid" class="btn btn-primary">Edit Code</router-link>
                <router-link :to="'/code/' + this.codeid + '/analytics'" class="btn btn-primary">View Analytics</router-link>
            </div>
        </section>

        <section class="contentGrid">
            <article class="detailCard">
                <h2>Details</h2>
                <div class="detailRows">
                    <div class="detailRow">
                        <div class="detailLabel">Owner</div>
                        <div class="detailValue">
                            <div>{{ ownername || "Unknown Owner" }}</div>
                            <div v-if="ownerusername" class="subtleLine">{{ ownerusername }}</div>
                            <div v-if="owneruserid" class="subtleLine">{{ owneruserid }}</div>
                        </div>
                    </div>
                    <div class="detailRow">
                        <div class="detailLabel">Shortcode URL</div>
                        <div class="detailValue">
                            <a :href="codeadurl" target="_blank" rel="noopener noreferrer">{{ codeadurl }}</a>
                        </div>
                    </div>
                    <div class="detailRow">
                        <div class="detailLabel">Code ID</div>
                        <div class="detailValue monoValue">{{ code.codeid }}</div>
                    </div>
                    <div class="detailRow">
                        <div class="detailLabel">Database ID</div>
                        <div class="detailValue monoValue">{{ code.id }}</div>
                    </div>
                    <div class="detailRow">
                        <div class="detailLabel">Shortcode</div>
                        <div class="detailValue monoValue">{{ shortcodeValue }}</div>
                    </div>
                    <div v-if="redirectTargetUrl" class="detailRow">
                        <div class="detailLabel">{{ code.selectedStyleSelectOption == 1 ? "Redirect URL" : "Web URL" }}</div>
                        <div class="detailValue">
                            <a :href="redirectTargetUrl" target="_blank" rel="noopener noreferrer">{{ redirectTargetUrl }}</a>
                        </div>
                    </div>
                    <div v-if="code.phone" class="detailRow">
                        <div class="detailLabel">Phone</div>
                        <div class="detailValue">{{ code.phone }}</div>
                    </div>
                    <div v-if="code.location" class="detailRow">
                        <div class="detailLabel">Location</div>
                        <div class="detailValue">{{ code.location }}</div>
                    </div>
                    <div v-if="code.upc" class="detailRow">
                        <div class="detailLabel">GTIN</div>
                        <div class="detailValue monoValue">{{ code.upc }}</div>
                    </div>
                    <div v-if="code.gs1?.digitalLinkUri" class="detailRow">
                        <div class="detailLabel">GS1 Digital Link</div>
                        <div class="detailValue">
                            <a :href="code.gs1.digitalLinkUri" target="_blank" rel="noopener noreferrer">{{ code.gs1.digitalLinkUri }}</a>
                        </div>
                    </div>
                    <div v-if="code.gs1?.elementString" class="detailRow">
                        <div class="detailLabel">GS1 Element String</div>
                        <div class="detailValue monoValue">{{ code.gs1.elementString }}</div>
                    </div>
                    <div v-if="gs1AiEntries.length > 0" class="detailRow">
                        <div class="detailLabel">GS1 AI Values</div>
                        <div class="detailValue">
                            <div v-for="entry in gs1AiEntries" :key="`${entry.ai}-${entry.value}`" class="subtleLine">
                                <strong>({{ entry.ai }})</strong> {{ entry.value }}
                            </div>
                        </div>
                    </div>
                </div>
            </article>

            <article class="detailCard">
                <h2>Description</h2>
                <div v-if="code.description" class="markdownDescription" v-html="renderedDescription"></div>
                <p v-else class="emptyState">No description provided for this code.</p>
            </article>
        </section>

        <section class="assetSection">
            <article class="assetCard assetCardQr">
                <div class="assetHeader">
                    <div>
                        <h2>QR Code</h2>
                        <p>Primary redirect asset</p>
                    </div>
                    <a :href="qrcodeurl" target="_blank" rel="noopener noreferrer" class="assetLink">Open image</a>
                </div>
                <div class="assetPreview assetPreviewQr">
                    <div class="assetPreviewContent">
                        <img :src="qrcodeurl" alt="QR Code" />
                        <div v-if="qrEncodedValue" class="assetEncodedValue">{{ qrEncodedValue }}</div>
                    </div>
                </div>
            </article>

            <article v-if="code.upc && gtinurl" class="assetCard">
                <div class="assetHeader">
                    <div>
                        <h2>GTIN Barcode</h2>
                        <p>Standalone product barcode</p>
                    </div>
                    <a :href="gtinurl" target="_blank" rel="noopener noreferrer" class="assetLink">Open image</a>
                </div>
                <div class="assetPreview">
                    <img :src="gtinurl" alt="GTIN Barcode" />
                </div>
            </article>

            <article v-if="code.upc && combinedbarcodeurl" class="assetCard assetCardWide">
                <div class="assetHeader">
                    <div>
                        <h2>Combined QR/GTIN</h2>
                        <p>Packaged asset for product-facing placement</p>
                    </div>
                    <a :href="combinedbarcodeurl" target="_blank" rel="noopener noreferrer" class="assetLink">Open image</a>
                </div>
                <div class="assetPreview assetPreviewWide">
                    <div class="assetPreviewContent">
                        <img :src="combinedbarcodeurl" alt="Combined QR and GTIN Barcode" />
                        <div v-if="combinedQrEncodedValue" class="assetEncodedValue">{{ combinedQrEncodedValue }}</div>
                    </div>
                </div>
            </article>

            <article v-if="gs1qrcodeurl" class="assetCard">
                <div class="assetHeader">
                    <div>
                        <h2>GS1 QR Code</h2>
                        <p>Digital Link QR generated from the stored GS1 payload</p>
                    </div>
                    <a :href="gs1qrcodeurl" target="_blank" rel="noopener noreferrer" class="assetLink">Open image</a>
                </div>
                <div class="assetPreview assetPreviewQr">
                    <div class="assetPreviewContent">
                        <img :src="gs1qrcodeurl" alt="GS1 QR Code" />
                        <div v-if="gs1QrEncodedValue" class="assetEncodedValue">{{ gs1QrEncodedValue }}</div>
                    </div>
                </div>
            </article>

            <article v-if="gs1datamatrixurl" class="assetCard">
                <div class="assetHeader">
                    <div>
                        <h2>GS1 DataMatrix</h2>
                        <p>GS1 AI element string encoded as a DataMatrix symbol</p>
                    </div>
                    <a :href="gs1datamatrixurl" target="_blank" rel="noopener noreferrer" class="assetLink">Open image</a>
                </div>
                <div class="assetPreview">
                    <div class="assetPreviewContent">
                        <img :src="gs1datamatrixurl" alt="GS1 DataMatrix" />
                        <div v-if="gs1DataMatrixEncodedValue" class="assetEncodedValue">{{ gs1DataMatrixEncodedValue }}</div>
                    </div>
                </div>
            </article>
        </section>
    </div>
    <div v-else class="loadingState">
        <div class="loadingCard">
            <h1>Loading code details</h1>
            <p>{{ codeidQuery || codeid }}</p>
        </div>
    </div>
</template>

<script>
import { getUser, getUserFullNameFromApi, getGs1AiEntries, normalizeCodeFromApi, getQrCodeUrlFromApi, getGtinUrlFromApi, getCombinedBarcodeUrlFromApi, getGs1QrUrlFromApi, getGs1DataMatrixUrlFromApi, getCodeUrlByShortcode, getSingleCodeFromApi } from "../../libs/libs.js"
import { apiFetchJson } from "@/libs/api"

const styleSelectOptions = [
    {
        name: "Redirect Only",
        value: 1
    },
    {
        name: "Traditional",
        value: 2
    },
]


export default {
    name: 'CodeDetails',
    display: "Codes Details",
    props: ["codeid"],
    data() {
        return { 
            description: "View Code",
            imgsrc: "",
            code: null,
            user: null,
            codeidQuery: this.codeid,
            codeadurl: null,
            qrcodeurl: null,
            gtinurl: null,
            combinedbarcodeurl: null,
            gs1qrcodeurl: null,
            gs1datamatrixurl: null,
            outputFormat: this.$route.query.of,
            codeStyle: 'None',
            owneruserid: '',
            ownerusername: '',
            ownername: '',
            displayUserStuffDetail: false,
            dtOptions: {
                year: 'numeric', 
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric', timeZoneName: 'short'
            }
        }
    },
    computed: {
        shortcodeValue() {
            return this.code?.shortcode || this.code?.shortcodeid || "";
        },
        redirectTargetUrl() {
            return this.code?.redirecturl || this.code?.desturl || "";
        },
        qrEncodedValue() {
            return this.codeadurl || "";
        },
        combinedQrEncodedValue() {
            const enabled = this.code?.metadata?.gs1Support === true;
            return enabled ? this.gs1QrEncodedValue : this.qrEncodedValue;
        },
        gs1QrEncodedValue() {
            return this.code?.gs1?.digitalLinkUri || "";
        },
        gs1DataMatrixEncodedValue() {
            return this.code?.gs1?.elementString || "";
        },
        formattedLastModified() {
            if (!this.code?.lastmodify) return "Unknown";
            return Intl.DateTimeFormat("en-US", this.dtOptions).format(new Date(this.code.lastmodify));
        },
        renderedDescription() {
            return this.renderMarkdown(this.code?.description || "");
        },
        gs1AiEntries() {
            return getGs1AiEntries(this.code);
        },
    },
    methods: {
        handleDisplayUserStuffDetail() {
            this.displayUserStuffDetail = !this.displayUserStuffDetail
        },
        jsonPretty(val, indent = 2) {
            if (typeof val === "string") {
                val = JSON.parse(val)
            }
            return JSON.stringify(val, null, indent)
        },
        renderMarkdown(value) {
            const escapeHtml = (input) =>
                `${input ?? ""}`
                    .replace(/&/g, "&amp;")
                    .replace(/</g, "&lt;")
                    .replace(/>/g, "&gt;")
                    .replace(/"/g, "&quot;")
                    .replace(/'/g, "&#39;");

            const formatInline = (line) => {
                let out = escapeHtml(line);
                out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
                out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
                out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
                out = out.replace(/\*([^*]+)\*/g, "<em>$1</em>");
                return out;
            };

            const lines = `${value ?? ""}`.split(/\r?\n/);
            const html = [];
            let inList = false;

            const closeList = () => {
                if (inList) {
                    html.push("</ul>");
                    inList = false;
                }
            };

            for (const rawLine of lines) {
                const line = rawLine.trim();
                if (!line) {
                    closeList();
                    continue;
                }
                if (/^[-*]\s+/.test(line)) {
                    if (!inList) {
                        html.push("<ul>");
                        inList = true;
                    }
                    html.push(`<li>${formatInline(line.replace(/^[-*]\s+/, ""))}</li>`);
                    continue;
                }
                closeList();
                const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
                if (headingMatch) {
                    const level = headingMatch[1].length;
                    html.push(`<h${level}>${formatInline(headingMatch[2])}</h${level}>`);
                    continue;
                }
                if (/^>\s+/.test(line)) {
                    html.push(`<blockquote>${formatInline(line.replace(/^>\s+/, ""))}</blockquote>`);
                    continue;
                }
                html.push(`<p>${formatInline(line)}</p>`);
            }
            closeList();
            return html.join("");
        },

    },
    async mounted() {
        if (this.outputFormat === 1) {
            console.log("outputFormat 1")
        } else {
            console.log("no outputFormat")
        }

        this.codeadurl = "https://qrexl.ink/"
        let url = getCodeUrlByShortcode(this.codeidQuery)
        console.log(`outputFormat: ${this.outputFormat}, url: ${url}, `)
        apiFetchJson(url, {}, { auth: true })
            .then(data => {
                this.code = normalizeCodeFromApi(getSingleCodeFromApi(data))
            })
            .then(() => {
                console.log(`code loaded ${JSON.stringify(this.code)}`)
                const shortcode = this.code.shortcode || this.code.shortcodeid
                this.codeadurl = shortcode ? `https://qrexl.ink/${shortcode}` : "https://qrexl.ink/"
                this.qrcodeurl = getQrCodeUrlFromApi(this.code)
                if (this.code.upc) {
                    this.gtinurl = getGtinUrlFromApi(this.code)
                    this.combinedbarcodeurl = getCombinedBarcodeUrlFromApi(this.code)
                }
                this.gs1qrcodeurl = getGs1QrUrlFromApi(this.code)
                this.gs1datamatrixurl = getGs1DataMatrixUrlFromApi(this.code)
                this.codeStyle = this.code && this.code.selectedStyleSelectOption ? styleSelectOptions.find(styleSelectOption => {
                    return styleSelectOption.value ==
                        this.code.selectedStyleSelectOption
                }).name : 'None';
            })
            .then(async () => {
                console.log(`code owneruserid ${this.code.owneruserid}`)
                this.user = await getUser(this.code.owneruserid)
                this.owneruserid = this.user.id
                this.ownerusername = this.user.username
                this.ownername = getUserFullNameFromApi(this.user)
            })
            .catch(err => {
                console.log(err.message)
            })
        console.log(`code data loaded...`);

    },
    components: {

    }

}
</script>

<style>
.codeDetailsPage {
    max-width: 1180px;
    margin: 22px auto 36px;
    padding: 0 16px;
}

.codeHero {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1.7fr) minmax(280px, 0.9fr);
    gap: 24px;
    padding: 28px;
    border-radius: 24px;
    background:
        radial-gradient(circle at top right, rgba(29, 78, 216, 0.14), transparent 30%),
        linear-gradient(135deg, #ffffff 0%, #eef4ff 55%, #e0edff 100%);
    border: 1px solid rgba(29, 78, 216, 0.16);
    box-shadow: 0 24px 56px rgba(15, 23, 42, 0.10);
    overflow: hidden;
}

.eyebrow {
    display: inline-block;
    margin-bottom: 10px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(29, 78, 216, 0.12);
    color: var(--qrce-primary-600);
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.heroCopy h1 {
    margin: 0 0 12px;
    font-size: clamp(2rem, 4vw, 3.4rem);
    line-height: 0.95;
    letter-spacing: -0.04em;
}

.heroDescription {
    max-width: 62ch;
    margin: 0;
    font-size: 1.05rem;
    line-height: 1.65;
    color: var(--qrce-muted);
}

.heroMeta {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 18px;
}

.metaChip {
    display: inline-flex;
    align-items: center;
    min-height: 34px;
    padding: 0 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid rgba(15, 23, 42, 0.08);
    color: var(--qrce-text);
    font-size: 0.92rem;
}

.heroActions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: center;
    align-items: stretch;
}

.heroActions .btn {
    font-weight: 700;
    padding: 12px 16px;
    border-radius: 14px;
}

.contentGrid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 18px;
    margin-top: 18px;
}

.detailCard,
.assetCard,
.loadingCard {
    background: var(--qrce-surface);
    border: 1px solid var(--qrce-border);
    border-radius: 22px;
    box-shadow: var(--qrce-shadow);
}

.detailCard {
    padding: 22px;
}

.detailCard h2,
.assetCard h2 {
    margin: 0 0 16px;
    font-size: 1.1rem;
    letter-spacing: -0.02em;
}

.detailRows {
    display: grid;
    gap: 12px;
}

.detailRow {
    display: grid;
    grid-template-columns: 160px minmax(0, 1fr);
    gap: 18px;
    padding: 14px 0;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.detailRow:first-child {
    border-top: 0;
    padding-top: 0;
}

.detailLabel {
    color: var(--qrce-muted);
    font-size: 0.84rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

.detailValue {
    overflow-wrap: anywhere;
    line-height: 1.55;
}

.detailValue a,
.assetLink {
    color: var(--qrce-primary);
    text-decoration: none;
}

.detailValue a:hover,
.assetLink:hover {
    text-decoration: underline;
}

.monoValue {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.94rem;
}

.subtleLine {
    color: var(--qrce-muted);
}

.markdownDescription {
    color: var(--qrce-text);
    line-height: 1.7;
}

.markdownDescription p,
.markdownDescription ul,
.markdownDescription blockquote,
.markdownDescription h1,
.markdownDescription h2,
.markdownDescription h3 {
    margin: 0 0 12px;
}

.markdownDescription ul {
    padding-left: 18px;
}

.markdownDescription blockquote {
    padding: 10px 14px;
    border-left: 3px solid var(--qrce-primary);
    background: rgba(29, 78, 216, 0.06);
    border-radius: 0 12px 12px 0;
    color: var(--qrce-muted);
}

.markdownDescription code {
    padding: 2px 6px;
    border-radius: 8px;
    background: rgba(15, 23, 42, 0.08);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.92em;
}

.emptyState {
    margin: 0;
    color: var(--qrce-muted);
}

.assetSection {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
    margin-top: 18px;
}

.assetCard {
    padding: 20px;
}

.assetCardWide {
    grid-column: 1 / -1;
}

.assetCardQr {
    background:
        linear-gradient(180deg, rgba(29, 78, 216, 0.06), transparent 55%),
        var(--qrce-surface);
}

.assetHeader {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: start;
    margin-bottom: 16px;
}

.assetHeader p {
    margin: 4px 0 0;
    color: var(--qrce-muted);
}

.assetPreview {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 220px;
    padding: 18px;
    border-radius: 18px;
    background:
        linear-gradient(135deg, rgba(15, 23, 42, 0.02), rgba(29, 78, 216, 0.06));
    border: 1px solid rgba(15, 23, 42, 0.06);
}

.assetPreviewContent {
    display: grid;
    justify-items: center;
    gap: 12px;
    width: 100%;
}

.assetPreview img {
    max-width: 100%;
    height: auto;
}

.assetEncodedValue {
    width: min(100%, 720px);
    overflow-wrap: anywhere;
    word-break: break-word;
    text-align: center;
    color: var(--qrce-muted);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.92rem;
    line-height: 1.4;
}

.assetPreviewQr img {
    width: min(100%, 280px);
}

.assetPreviewWide img {
    width: min(100%, 720px);
}

.loadingState {
    padding: 28px 16px;
}

.loadingCard {
    max-width: 760px;
    margin: 0 auto;
    padding: 28px;
    text-align: center;
}

.loadingCard h1 {
    margin: 0 0 8px;
}

@media (max-width: 959px) {
    .codeHero,
    .contentGrid,
    .assetSection {
        grid-template-columns: 1fr;
    }

    .heroActions {
        align-items: stretch;
    }
}

@media (max-width: 639px) {
    .codeDetailsPage {
        margin-top: 16px;
        padding: 0 12px;
    }

    .codeHero,
    .detailCard,
    .assetCard,
    .loadingCard {
        border-radius: 18px;
    }

    .codeHero {
        padding: 22px 18px;
    }

    .detailRow {
        grid-template-columns: 1fr;
        gap: 8px;
    }

    .heroActions .btn {
        width: 100%;
    }
}
</style>
